/* Plyr on every video in this PDP — gallery slides, the hero video rail and
   the review cards.

   Matching doreandrose.com, which runs the same library. Their configuration
   is readable from the classes and data-plyr attributes their markup carries:

     controls      play-large, play, progress (seek), mute, volume, fullscreen
     hideControls  on — .plyr__controls fades after ~2s idle, returns on hover
     <video>       playsinline, preload="metadata", no `controls` attribute

   Why a library rather than native controls: iOS renders its own cluster
   (fullscreen, AirPlay, PiP) that cannot be pared back — controlsList is
   Chromium-only and the ::-webkit-media-controls hooks do not apply on iOS.
   Taking `controls` off is the only way to own what is on screen, and it also
   removes the layer that was swallowing touchmove and breaking the gallery
   swipe.

   Icons come from the sprite that snippets/reformly-bp-plyr-icons.liquid
   inlines into the page, NOT from a URL. Plyr defaults iconUrl to
   https://cdn.plyr.io/…/plyr.svg — an external CDN — and even pointed at a
   theme asset it is still a request the bar has to wait on, which showed up as
   empty control buttons for a beat on a phone. */
(function () {
  'use strict';

  /* The hero's video rail (.rbp-vcard) is deliberately absent: those are ~120px
     thumbnails in a 56-clip strip and the control bar is 44px tall, so it
     covered the clip. They keep the native controls their markup already asks
     for. */
  var SURFACES = [
    '.rbp-gal__slide video',   // hero gallery
    '.rbp-card__media video',  // review cards
  ];

  var OPTIONS = {
    /* No 'volume'. Theirs renders the slider as a hover popup that is
       invisible at rest (measured: .plyr__volume is 140x40 at opacity 0,
       absolutely positioned off the bar), so their bar shows a mute toggle
       only. Including it inline is what put a second purple slider in ours. */
    controls: ['play-large', 'play', 'progress', 'mute', 'fullscreen'],
    hideControls: true,
    /* Off: on this PDP a click on the gallery pages the carousel, and the
       paging arrow follows the cursor across the video. With clickToPlay on,
       the same click would also toggle playback. Play/pause lives on the bar. */
    clickToPlay: false,
    resetOnEnd: false,
    /* Empty, deliberately. Plyr builds `${iconUrl}#plyr-play`, so '' yields a
       same-document reference into the sprite that
       snippets/reformly-bp-plyr-icons.liquid inlines — no fetch, so the bar is
       never drawn with empty buttons while a request is in flight. */
    iconUrl: '',
    // The poster is our own <img> behind the video on every surface, so Plyr
    // must not draw a second one over it.
    loadSprite: false,
    ratio: null,
    /* iosNative is the whole point of this block. iPhone Safari has no element
       fullscreen API — only <video>.webkitEnterFullscreen() — so Plyr's default
       is a `fallback` mode that fixes the player to the viewport with CSS. That
       cannot escape the gallery, whose track is transformed and whose slides
       clip overflow, so the button did nothing on a phone. With iosNative on,
       Plyr calls webkitEnterFullscreen and the OS player takes over.

       iPad reports as a desktop UA and does have the real API, so it is
       unaffected by this and keeps the normal path. */
    fullscreen: { enabled: true, fallback: true, iosNative: true },
    tooltips: { controls: false, seek: true },
    keyboard: { focused: true, global: false },
    /* Off. Plyr persists volume/muted to localStorage and reads it back on
       every init, so one unmute anywhere on the site would carry into the
       gallery on the next visit and undo the muted-by-default below. */
    storage: { enabled: false },
  };

  function init(scope) {
    if (typeof window.Plyr !== 'function') return;
    var root = scope || document;

    SURFACES.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (v) {
        // Plyr moves the element into its own wrapper; running twice would
        // nest wrappers and detach our positioning.
        if (v.dataset.rbpPlyr) return;
        v.dataset.rbpPlyr = '1';

        /* Native controls off. They are still on the tag so the video is
           usable if this script fails to load — Plyr strips the attribute
           itself, but doing it here keeps the two in step when it does not
           run at all. */
        v.removeAttribute('controls');

        try {
          /* muted comes from the TAG, not from OPTIONS.

             Plyr's media setup assigns `this.muted = null`, and the setter
             falls through null -> storage -> config.muted, which defaults to
             false — so it actively unmutes an element authored muted. The
             gallery renders its clips with muted: true (reformly-bp-hero.liquid)
             precisely so a visitor opening a video is not startled, and Plyr
             was undoing that.

             Reading it off the element keeps the two surfaces different without
             a lookup table: the gallery stays silent until someone unmutes it,
             while the review-card clips (authored unmuted, and only ever
             started by a deliberate tap on the play button) keep their audio. */
          new window.Plyr(v, Object.assign({}, OPTIONS, { muted: v.muted }));
        } catch (e) {
          // Put the native controls back rather than leaving a dead element.
          v.setAttribute('controls', '');
          delete v.dataset.rbpPlyr;
        }
      });
    });
  }

  /* Plyr is no longer on the page at boot — reformly-bp-player-boot.js fetches
     it on the first sign of intent to play. This file still loads immediately
     (it is 6KB) so the observers below are wired from the start; init() simply
     does nothing until window.Plyr exists, and the boot script calls back here
     once it does.

     Exposed on window rather than listening for a custom event so there is no
     ordering hazard: whichever of the two scripts parses second still works. */
  window.rbpPlayerInit = function () { init(); };

  function boot() {
    init();
    /* The colour swatches rebuild the gallery's slides, and the theme editor
       re-renders whole sections — both produce <video> elements that never
       passed through the initial run. */
    document.querySelectorAll('[data-rbp-gal]').forEach(function (g) {
      new MutationObserver(function () { init(g); })
        .observe(g, { childList: true, subtree: true });
    });
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
